import React, { useEffect, useMemo, useState } from "react";
import {
    Offcanvas,
    Form,
    Button,
    Tab,
    Tabs,
    Spinner,
    Row,
    Col,
    Badge,
} from "react-bootstrap";
import "bootstrap/dist/css/bootstrap.min.css";
import LinkedItemBox from "../../components/LinkedItemBox";
import Select from "react-select";
import WeekDaysSelector from "../../components/WeekDaysSelector";
import { useNavigate } from "react-router";
import { gql, useMutation, useQuery } from "@apollo/client";
import ConfirmModal from "../../components/modals/ConfirmModal";

function AddShiftSidebar(props) {
    const CREATE_ONE_SHIFT = gql`
        mutation CreateOneShift($data: ShiftCreateInput!) {
            createOneShift(data: $data) {
                id
                name
                description
                start_time
                end_time
                runs_on
                created_at
            }
        }
    `;

    const UPDATE_ONE_SHIFT = gql`
        mutation UpdateOneShift(
            $data: ShiftUpdateInput!
            $where: ShiftWhereUniqueInput!
        ) {
            updateOneShift(data: $data, where: $where) {
                id
                name
                description
                start_time
                end_time
                runs_on
                updated_at
            }
        }
    `;

    const DELETE_ONE_SHIFT = gql`
        mutation DeleteOneShift($id: Int!) {
            deleteOneShift(where: { id: $id }) {
                id
            }
        }
    `;

    // Confirmation messages
    const SHIFT_MESSAGES = {
        DELETE_SHIFT: (
            shiftName
        ) => `You are about to delete shift "${shiftName}".

This action cannot be undone.

Are you sure you wish to continue?`,
    };

    const navigator = useNavigate();
    const [isBtnLoading, setIsBtnLoading] = useState(false);
    // const [selectedUsers, setSelectedUsers] = useState([]);
    const [weekDays, setWeekDays] = useState(62);

    const [startSec, setStartSec] = useState(8 * 3600);
    const [durSec, setDurSec] = useState(9 * 3600);
    const [confirmCfg, setConfirmCfg] = useState({ open: false });
    const [confirmLoading, setConfirmLoading] = useState(false);
    const client = props.starekContext.engineClient;
    const userId = parseInt(props.starekContext.user?.id ?? 2);

    const [createShift] = useMutation(CREATE_ONE_SHIFT, { client });
    const [updateShift] = useMutation(UPDATE_ONE_SHIFT, { client });
    const [deleteShift] = useMutation(DELETE_ONE_SHIFT, { client });
    const [activeTab, setActiveTab] = useState("basic_details");

    // const {
    //   data: usersData,
    //   loading: usersLoading,
    //   error: usersError,
    // } = useQuery(GET_USERS, { client, fetchPolicy: "cache-and-network" });

    // const userOptions = useMemo(() => {
    //   const list = usersData?.users ?? [];
    //   return list
    //     .filter((u) => u?.is_active !== false)
    //     .filter((u) => Number(u.id) !== 1) // exclude user id 1
    //     .map((u) => ({
    //       value: String(u.id),
    //       label: u.name?.trim() ? `${u.name} <${u.email}>` : u.email,
    //     }));
    // }, [usersData]);

    // useEffect(() => {
    //   if (props.item?.notified_users?.length && userOptions.length) {
    //     const mapped = props.item.notified_users
    //       .map((u) => userOptions.find((opt) => opt.value === String(u.id)))
    //       .filter(Boolean);
    //     setSelectedUsers(mapped);
    //   }
    // }, [props.item?.notified_users, userOptions]);

    const fmt12h = (s) => {
        const hh = Math.floor(s / 3600);
        const mm = Math.floor((s % 3600) / 60);
        const h12 = ((hh + 11) % 12) + 1;
        const ampm = hh < 12 ? "AM" : "PM";
        return `${h12}:${mm.toString().padStart(2, "0")} ${ampm}`;
    };
    const toSec = (h, m) => h * 3600 + m * 60;
    const endSec = useMemo(
        () => (startSec + durSec) % 86400,
        [startSec, durSec]
    );
    const crossesMidnight = useMemo(
        () => startSec + durSec >= 86400,
        [startSec, durSec]
    );

    useEffect(() => {
        if (props.item?.start_time != null && props.item?.end_time != null) {
            const s = Number(props.item.start_time) || 0;
            const e = Number(props.item.end_time) || 0;
            setStartSec(s);
            const d = (e - s + 86400) % 86400; // overnight
            setDurSec(d);
        }
    }, [props.item]);

    const startTimeOptions = useMemo(() => {
        const opts = [];
        for (let h = 0; h < 24; h++) {
            for (let m of [0, 30]) {
                const s = toSec(h, m);
                opts.push({ value: s, label: fmt12h(s) });
            }
        }
        return opts;
    }, []);

    const durationOptions = useMemo(() => {
        const opts = [];
        for (let m = 15; m <= 24 * 60; m += 15) {
            const h = Math.floor(m / 60);
            const mm = m % 60;
            const label =
                h > 0 && mm > 0
                    ? `${h} hr ${mm} min`
                    : h > 0
                    ? `${h} hr${h === 1 ? "" : "s"}`
                    : `${mm} min`;
            opts.push({ value: m * 60, label });
        }
        return opts;
    }, []);

    const handleSave = async (event) => {
        setIsBtnLoading(true);
        event.preventDefault();

        const formData = new FormData(event.target);
        const formValues = {
            name: (formData.get("shift_name") || "").toString().trim(),
            description: (formData.get("shift_description") || "")
                .toString()
                .trim(),
            week_days: weekDays, // bitmask
            start_time: startSec, // seconds since midnight
            end_time: endSec, // seconds since midnight (handles rollover)
        };

        try {
            if (!props.item?.id) {
                await createShift({
                    variables: {
                        data: {
                            name: formValues.name,
                            description: formValues.description,
                            start_time: formValues.start_time,
                            end_time: formValues.end_time,
                            runs_on: formValues.week_days,
                            plant: {
                                connect: {
                                    id: props.starekContext.lastUsedPlant.id,
                                },
                            },
                            created_by: { connect: { id: userId } },
                            updated_by: { connect: { id: userId } },
                        },
                    },
                });
            } else {
                await updateShift({
                    variables: {
                        where: { id: props.item.id },
                        data: {
                            name: { set: formValues.name },
                            description: { set: formValues.description },
                            start_time: { set: formValues.start_time },
                            end_time: { set: formValues.end_time },
                            runs_on: { set: formValues.week_days },
                            updated_by: { connect: { id: userId } },
                        },
                    },
                });
            }

            props.closeOffcanvas(true, null);
        } catch (error) {
            props.starekContext.showError(error);
        } finally {
            setIsBtnLoading(false);
        }
    };

    const handleDiscardBtnClick = () => {
        props.closeOffcanvas(true, null);
    };

    const openConfirm = (cfg) => setConfirmCfg({ open: true, ...cfg });
    const closeConfirm = () => setConfirmCfg({ open: false });

    const handleDeleteClick = () => {
        if (!props.item?.id) return;
        openConfirm({
            title: "Delete Shift?",
            message: SHIFT_MESSAGES.DELETE_SHIFT(props.item.name),
            confirmText: "Delete",
            cancelText: "Cancel",
            type: "danger",
            onConfirm: handleDelete,
            onClose: closeConfirm,
        });
    };

    const handleDelete = async () => {
        if (!props.item?.id) return;
        setConfirmLoading(true);
        try {
            await deleteShift({
                variables: { id: parseInt(props.item.id, 10) },
            });
            props.closeOffcanvas(true, null);
        } catch (error) {
            props.starekContext.showError(error);
        } finally {
            setConfirmLoading(false);
            closeConfirm();
        }
    };

    return (
        <Offcanvas
            show={props.show}
            onHide={props.closeOffcanvas}
            placement="end"
        >
            <Offcanvas.Header closeButton>
                <Offcanvas.Title>
                    {props.item ? "Edit Shift" : "Add Shift"}
                </Offcanvas.Title>
            </Offcanvas.Header>

            <Offcanvas.Body className="d-flex flex-column">
                <Form
                    className="mt-4 font-weight-normal d-flex flex-column flex-grow-1 h-100"
                    onSubmit={(e) => {
                        e.preventDefault();
                        // if (activeTab === "basic_details") setActiveTab("zones");
                        handleSave(e);
                    }}
                >
                    <Tabs
                        activeKey={activeTab}
                        onSelect={(k) => setActiveTab(k)}
                        id="add_shift"
                        className="border-top py-1"
                    >
                        <Tab
                            eventKey="basic_details"
                            title={
                                <div className="d-flex align-items-center">
                                    <i className="fa fa-clipboard mr-1"></i>
                                    <span>Basic Details</span>
                                </div>
                            }
                        >
                            <Form.Group className="mb-3" controlId="shift_name">
                                <Form.Label>
                                    Name <span className="text-danger">*</span>
                                </Form.Label>
                                <Form.Control
                                    type="text"
                                    name="shift_name"
                                    placeholder="Enter here"
                                    defaultValue={props.item?.name || ""}
                                />
                            </Form.Group>

                            <Form.Group
                                className="mb-3"
                                controlId="shift_description"
                            >
                                <Form.Label>Description</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    name="shift_description"
                                    rows={3}
                                    placeholder="Max allowed characters: 120"
                                    defaultValue={props.item?.description || ""}
                                />
                            </Form.Group>

                            <div className="border p-2 mt-2">
                                <div className="d-flex align-items-center justify-content-between mb-3">
                                    <div>Timings</div>
                                    <div className="d-flex align-items-center">
                                        <WeekDaysSelector
                                            initialValue={weekDays}
                                            onChange={(days) =>
                                                setWeekDays(days)
                                            }
                                        />
                                    </div>
                                </div>

                                <Row className="g-2">
                                    <Col md={6}>
                                        <Form.Group controlId="start_time_select">
                                            <Form.Label>Start time</Form.Label>
                                            <Select
                                                inputId="start_time_select"
                                                options={startTimeOptions}
                                                value={startTimeOptions.find(
                                                    (o) => o.value === startSec
                                                )}
                                                onChange={(o) =>
                                                    o && setStartSec(o.value)
                                                }
                                                isClearable={false}
                                            />
                                        </Form.Group>
                                    </Col>

                                    <Col md={6}>
                                        <Form.Group controlId="duration_select">
                                            <Form.Label>Duration</Form.Label>
                                            <Select
                                                inputId="duration_select"
                                                options={durationOptions}
                                                value={
                                                    durationOptions.find(
                                                        (o) =>
                                                            o.value === durSec
                                                    ) || null
                                                }
                                                onChange={(o) =>
                                                    o && setDurSec(o.value)
                                                }
                                                isClearable={false}
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <div className="mt-2 small">
                                    <strong>Preview:</strong> {fmt12h(startSec)}{" "}
                                    → {fmt12h(endSec)}{" "}
                                    {crossesMidnight && (
                                        <Badge bg="secondary">next day</Badge>
                                    )}
                                </div>
                            </div>
                        </Tab>

                        {/* <Tab
              eventKey="zones"
              title={
                <div className="d-flex align-items-center">
                  <i className="fa fa-envelope mr-1"></i>
                  <span>Email Notifications</span>
                </div>
              }
            >
              <div className="my-3">
                Attach users to notify when this shift completes.
              </div>

              {usersError && (
                <div className="text-danger small mb-2">
                  Failed to load users. Please try again.
                </div>
              )}

              <Form.Group className="mb-3" controlId="shift_users">
                <Form.Label>Users</Form.Label>
                <Select
                  id="users"
                  isMulti
                  options={userOptions}
                  placeholder={
                    usersLoading ? "Loading users..." : "Select users"
                  }
                  isClearable
                  isDisabled={usersLoading}
                  value={selectedUsers}
                  onChange={setSelectedUsers}
                />
              </Form.Group>

              <div className="my-3">Linked Users</div>
              {selectedUsers.map((user) => (
                <LinkedItemBox
                  key={user.value}
                  icon={"fa-user-circle"}
                  label={user.label}
                />
              ))}
            </Tab> */}
                    </Tabs>

                    <div className="d-flex justify-content-between border-top pt-2 mt-auto mb-3">
                        <div>
                            {props.item && (
                                <Button
                                    variant="danger"
                                    className="ms-2"
                                    onClick={handleDeleteClick}
                                    disabled={isBtnLoading}
                                >
                                    Delete
                                </Button>
                            )}
                        </div>
                        <div>
                            <Button
                                variant="outline-danger"
                                onClick={handleDiscardBtnClick}
                            >
                                Discard
                            </Button>
                            <Button
                                className="ms-2"
                                variant="primary"
                                type="submit"
                                disabled={isBtnLoading}
                            >
                                {isBtnLoading && (
                                    <Spinner
                                        as="span"
                                        animation="border"
                                        size="sm"
                                        role="status"
                                        aria-hidden="true"
                                    />
                                )}{" "}
                                {props.item ? "Update Shift" : "Add Shift"}
                            </Button>
                        </div>
                    </div>
                </Form>
            </Offcanvas.Body>
            <ConfirmModal
                open={confirmCfg.open}
                title={confirmCfg.title}
                message={confirmCfg.message}
                confirmText={confirmCfg.confirmText}
                cancelText={confirmCfg.cancelText}
                type={confirmCfg.type}
                loading={confirmLoading}
                onConfirm={confirmCfg.onConfirm}
                onClose={confirmCfg.onClose}
            />
        </Offcanvas>
    );
}

export default AddShiftSidebar;
